import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Components
import Avatar from './Avatar'
import { Icon } from './icon'

// Styles
import { applyTextStyle } from '../styles/typography'
import { fastTransition, focusRing, shadows } from '../styles/effects'

// Types
import { ICompanyMemberPure } from '../common/models/Company'

interface IProps {
  label: string
  // A chip that names a person: their avatar fills the chip's left end
  member?: ICompanyMemberPure
  // Toggle chip: marks the value it stands for as the current one
  active?: boolean
  onClick?: () => void
  // Removable chip: shows a cross and removes what it names
  onRemove?: () => void
  className?: string
}

// Outlined pill for a value: a filter to remove, a preset to pick, or plain text
const Chip: React.FC<IProps> = ({
  label,
  member,
  active,
  onClick,
  onRemove,
  className,
}) => {
  const theme = useContext(ThemeContext)
  const content = (
    <>
      {member && (
        <Face>
          <Avatar user={member} />
        </Face>
      )}
      <ChipLabel $active={active}>{label}</ChipLabel>
      {onRemove && (
        <Icon type="Close" size={12} color={theme.colors.primaryFadeHalf} />
      )}
    </>
  )

  if (!onClick && !onRemove) {
    return (
      <StaticChip className={className} $withFace={!!member}>
        {content}
      </StaticChip>
    )
  }

  return (
    <ChipButton
      type="button"
      className={className}
      $active={active}
      $withFace={!!member}
      aria-pressed={active}
      aria-label={onRemove ? `Remove ${label}` : undefined}
      onClick={onRemove ?? onClick}
    >
      {content}
    </ChipButton>
  )
}

export default Chip

const chipStyles = css<{ $active?: boolean; $withFace?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  // With a face the chip has no room on its left: the avatar sits flush in the round end
  padding: ${({ $withFace }) => ($withFace ? '0 12px 0 0' : '4px 12px')};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.primary : theme.colors.text72};
  border-radius: 999px;
  background-color: ${({ theme, $active }) =>
    $active ? theme.colors.primaryLight : 'transparent'};
`

const StaticChip = styled.span<{ $withFace?: boolean }>`
  ${chipStyles}
`

const ChipButton = styled.button<{ $active?: boolean; $withFace?: boolean }>`
  ${chipStyles}
  cursor: pointer;
  transition: background-color ${fastTransition}, border-color ${fastTransition},
    box-shadow ${fastTransition};
  ${focusRing};

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
    box-shadow: ${shadows.depthTwo};
  }
`

// As high as the chip is without one (a line of text and its 4px above and below), so
// the chip keeps its height. The avatar fills it.
const Face = styled.span`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

const ChipLabel = styled.span<{ $active?: boolean }>`
  ${applyTextStyle('caption')}
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.textDark};
  white-space: nowrap;
`
